import { MembershipStatusPage } from "../features/dashboard/MembershipStatusPage";
import { PaymentAgencyPage } from "../features/payment-agency/PaymentAgencyPage";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  matchPath,
  useLocation,
  useMatches,
  useNavigate,
  useNavigationType,
  useParams,
  useRouteError,
} from "react-router";
import { useLayoutEffect } from "react";
import { useIsFetching } from "@tanstack/react-query";
import type { RouteObject } from "react-router";
import { Alert, Button } from "../ui";
import { LoginPage } from "../features/auth/LoginPage";
import { PasswordChangePage } from "../features/auth/PasswordChangePage";
import { TotpSetupPage } from "../features/auth/TotpSetupPage";
import { DashboardPage } from "../features/dashboard/DashboardPage";
import { MemberListPage } from "../features/members/MemberListPage";
import { MemberDetailPage } from "../features/members/MemberDetailPage";
import { TransactionListPage } from "../features/transactions/TransactionListPage";
import { TransactionDetailPage } from "../features/transactions/TransactionDetailPage";
import { TransferListPage } from "../features/transfers/TransferListPage";
import { TransferDetailPage } from "../features/transfers/TransferDetailPage";
import { AdministrativeQueuePage } from "../features/business-operations/AdministrativeQueuePage";
import { RefundDetailPage } from "../features/refunds/RefundDetailPage";

import { BenefitDetailPage } from "../features/benefits/BenefitDetailPage";

import { InquiryDetailPage } from "../features/inquiries/InquiryDetailPage";
import { NotificationPage } from "../features/notifications/NotificationPage";
import { NotificationDetailPage } from "../features/notifications/NotificationDetailPage";
import { ContentPage } from "../features/contents/ContentPage";
import { ContentDetailPage } from "../features/contents/ContentDetailPage";
import { ContentCreatePage } from "../features/contents/ContentCreatePage";
import { PartnerPage } from "../features/partners/PartnerPage";
import { IntegrationPage } from "../features/integrations/IntegrationPage";
import { IntegrationDetailPage } from "../features/integrations/IntegrationDetailPage";
import { ReconciliationPage } from "../features/reconciliations/ReconciliationPage";
import { ReconciliationRunDetailPage } from "../features/reconciliations/ReconciliationRunDetailPage";
import { ReconciliationIssueDetailPage } from "../features/reconciliations/ReconciliationIssueDetailPage";
import { LogsPage } from "../features/logs/LogsPage";
import { ExportPage } from "../features/exports/ExportPage";
import { ExportDetailPage } from "../features/exports/ExportDetailPage";
import { AdminUsersPage } from "../features/admin-users/AdminUsersPage";
import { IpAllowlistPage } from "../features/ip-allowlist/IpAllowlistPage";
import { BusinessOperationsPage } from "../features/business-operations/BusinessOperationsPage";
import { AdminLayout, AuthLayout, PageHeader, StateNotice } from "../features/workspace/shared";
import { canAccessAdminPage, defaultAdminPath, isRestrictedViewer } from "./permissions";
import { useAdminApplication, useAdminSession, useAdminUiStore } from "./providers";
import {
  ADMIN_NAVIGATION_GROUPS,
  ADMIN_ROUTE_DEFINITIONS,
  ADMIN_ROUTES_BY_ID,
  pathForAdminScreen,
  parseAdminEntityId,
  safeAdminReturnPath,
} from "./routes";
import type { AdminNavigationScreenId, AdminRouteDefinition } from "./routes";

interface AdminRouteHandle {
  readonly adminRoute: AdminRouteDefinition;
}

function useCurrentAdminRoute(): AdminRouteDefinition {
  const matches = useMatches();
  const handle = matches
    .map((match) => match.handle as AdminRouteHandle | undefined)
    .findLast((candidate) => candidate?.adminRoute);
  if (!handle) throw new Error("The matched route has no admin metadata.");
  return handle.adminRoute;
}

function phaseDestination(phase: ReturnType<typeof useAdminSession>["phase"]): string | null {
  if (phase === "password-change") return ADMIN_ROUTES_BY_ID["ADM-S02"].path;
  if (phase === "totp-enrollment") return ADMIN_ROUTES_BY_ID["ADM-S03"].path;
  return null;
}

function returnToSearch(search: string): string {
  const returnTo = safeAdminReturnPath(new URLSearchParams(search).get("returnTo"));
  return returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
}

function loginPathForLocation(location: { readonly pathname: string; readonly search: string }): string {
  const returnTo = `${location.pathname}${location.search}`;
  return `/login?returnTo=${encodeURIComponent(returnTo)}`;
}

function permittedReturnPath(
  rawValue: string | null,
  authorities: readonly string[],
): string | null {
  const safePath = safeAdminReturnPath(rawValue);
  if (!safePath) return null;
  const pathname = new URL(safePath, "https://carepass.invalid").pathname;
  const definition = ADMIN_ROUTE_DEFINITIONS.find((candidate) => (
    candidate.surface === "operations"
    && matchPath({ path: candidate.path, end: true }, pathname)
  ));
  return definition && canAccessAdminPage(definition.id, authorities, "operations")
    ? safePath
    : null;
}

function RoutePlaceholder({ definition }: { readonly definition: AdminRouteDefinition }) {
  if (definition.surface !== "operations") {
    return (
      <>
        <h1>{definition.title}</h1>
        <p>{definition.description}</p>
        <Alert persistent variant="information" title="페이지 연결 준비 완료">
          인증 단계의 실제 폼과 API 연결은 페이지 구현 단위에서 이어집니다.
        </Alert>
      </>
    );
  }
  return (
    <>
      <PageHeader
        id={definition.id}
        title={definition.title}
        description={definition.description}
      />
      <Alert persistent variant="information" title="공통 기반 연결 완료">
        이 URL의 세션, 권한, 레이아웃 경계가 적용됐습니다. 화면 데이터는 페이지 구현 단위에서 연결됩니다.
      </Alert>
    </>
  );
}

function AdminRouteEntry({ definition }: { readonly definition: AdminRouteDefinition }) {
  const params = useParams();
  const numericEntityInvalid = definition.entityParam
    && definition.entityParam !== "jobId"
    && parseAdminEntityId(params[definition.entityParam]) === null;
  const reconciliationParentInvalid = definition.id === "ADM-S31"
    && parseAdminEntityId(params.runId) === null;
  const exportJobInvalid = definition.entityParam === "jobId"
    && !params.jobId?.trim();
  if (numericEntityInvalid || reconciliationParentInvalid || exportJobInvalid) {
    return <StateNotice state="not-found" />;
  }
  if (definition.id === "ADM-S02") return <PasswordChangePage />;
  if (definition.id === "ADM-S03") return <TotpSetupPage />;
  if (definition.id === "ADM-S42") return <MembershipStatusPage />;
  if (definition.id === "ADM-S04") return <DashboardPage />;
  if (definition.id === "ADM-S05") return <MemberListPage />;
  if (definition.id === "ADM-S06") return <MemberDetailPage />;
  if (definition.id === "ADM-S07") return <TransactionListPage />;
  if (definition.id === "ADM-S08") return <TransactionDetailPage />;
  if (definition.id === "ADM-S09") return <TransferListPage />;
  if (definition.id === "ADM-S10") return <TransferDetailPage />;
  if (definition.id === "ADM-S11") return <AdministrativeQueuePage kind="REFUNDS" />;
  if (definition.id === "ADM-S12") return <AdministrativeQueuePage kind="BENEFITS" />;
  if (definition.id === "ADM-S13") return <AdministrativeQueuePage kind="INQUIRIES" />;
  if (definition.id === "ADM-S14") return <NotificationPage />;
  if (definition.id === "ADM-S15") return <ContentPage />;
  if (definition.id === "ADM-S16") return <PartnerPage />;
  if (definition.id === "ADM-S17") return <IntegrationPage />;
  if (definition.id === "ADM-S18") return <ReconciliationPage />;
  if (definition.id === "ADM-S19") return <LogsPage />;
  if (definition.id === "ADM-S20") return <ExportPage />;
  if (definition.id === "ADM-S21") return <AdminUsersPage />;
  if (definition.id === "ADM-S22") return <IpAllowlistPage />;
  if (definition.id === "ADM-S24") return <BenefitDetailPage />;
  if (definition.id === "ADM-S23") return <RefundDetailPage />;
  if (definition.id === "ADM-S25") return <InquiryDetailPage />;
  if (definition.id === "ADM-S26") return <NotificationDetailPage />;
  if (definition.id === "ADM-S27") return <ContentDetailPage />;
  if (definition.id === "ADM-S28") return <ContentCreatePage />;
  if (definition.id === "ADM-S29") return <IntegrationDetailPage />;
  if (definition.id === "ADM-S30") return <ReconciliationRunDetailPage />;
  if (definition.id === "ADM-S31") return <ReconciliationIssueDetailPage />;
  if (definition.id === "ADM-S32") return <ExportDetailPage />;
  if (definition.id === "ADM-S41") return <PaymentAgencyPage />;
  if (definition.id === "ADM-S33" || definition.id === "ADM-S34" || definition.id === "ADM-S35" || definition.id === "ADM-S36" || definition.id === "ADM-S37" || definition.id === "ADM-S38" || definition.id === "ADM-S39" || definition.id === "ADM-S40") {
    return <BusinessOperationsPage pageId={definition.id} />;
  }
  return <RoutePlaceholder definition={definition} />;
}

function AdminLoginRoute() {
  const session = useAdminSession();
  const location = useLocation();
  const destination = phaseDestination(session.phase);
  if (destination) return <Navigate replace to={`${destination}${returnToSearch(location.search)}`} />;
  if (session.phase === "operations") {
    const returnTo = permittedReturnPath(
      new URLSearchParams(location.search).get("returnTo"),
      session.authSession?.authorities ?? [],
    );
    return <Navigate replace to={returnTo ?? defaultAdminPath(session.authSession?.authorities ?? [])} />;
  }
  return (
    <AuthLayout step="login">
      <LoginPage />
    </AuthLayout>
  );
}

function AdminSetupLayout() {
  const session = useAdminSession();
  const definition = useCurrentAdminRoute();
  const location = useLocation();
  const destination = phaseDestination(session.phase);
  if (session.phase === "anonymous") {
    return <Navigate replace to={loginPathForLocation(location)} />;
  }
  if (session.phase === "operations") {
    const returnTo = permittedReturnPath(
      new URLSearchParams(location.search).get("returnTo"),
      session.authSession?.authorities ?? [],
    );
    return <Navigate replace to={returnTo ?? defaultAdminPath(session.authSession?.authorities ?? [])} />;
  }
  if (destination && destination !== definition.path) {
    return <Navigate replace to={`${destination}${returnToSearch(location.search)}`} />;
  }
  const authorities = session.authSession?.authorities ?? [];
  const allowed = canAccessAdminPage(definition.id, authorities, session.phase);
  const step = definition.id === "ADM-S02" ? "password" : "totp";
  return (
    <AuthLayout step={step}>
      {allowed ? <Outlet /> : <StateNotice state="denied" />}
    </AuthLayout>
  );
}

function visibleOperationScreens(authorities: readonly string[]): readonly AdminNavigationScreenId[] {
  const navigationScreens = new Set<AdminNavigationScreenId>(ADMIN_NAVIGATION_GROUPS.flatMap((group) => group.screens));
  return ADMIN_ROUTE_DEFINITIONS
    .filter((definition) => (
      definition.surface === "operations"
      && definition.navigationScreen === definition.screen
      && navigationScreens.has(definition.screen)
      && canAccessAdminPage(definition.id, authorities, "operations")
    ))
    .map((definition) => definition.navigationScreen);
}

function AdminNavigationEntryTracker({ definition }: { readonly definition: AdminRouteDefinition }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const recordNavigation = useAdminUiStore((state) => state.recordNavigation);
  const rememberMenuPath = useAdminUiStore((state) => state.rememberMenuPath);

  useLayoutEffect(() => {
    recordNavigation(location.key, navigationType);
    if (definition.screen === definition.navigationScreen) {
      const path = safeAdminReturnPath(`${location.pathname}${location.search}`);
      if (path) rememberMenuPath(definition.navigationScreen, path);
    }
  }, [definition.navigationScreen, definition.screen, location.key, location.pathname, location.search, navigationType, recordNavigation, rememberMenuPath]);

  return null;
}

function AdminOperationsLayout() {
  const { sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  const definition = useCurrentAdminRoute();
  const location = useLocation();
  const navigate = useNavigate();
  const menuPaths = useAdminUiStore((state) => state.menuPaths);
  const routeDataLoading = useIsFetching() > 0;
  const destination = phaseDestination(session.phase);
  if (session.phase === "anonymous") return <Navigate replace to={loginPathForLocation(location)} />;
  if (destination) return <Navigate replace to={`${destination}${returnToSearch(location.search)}`} />;
  const authorities = session.authSession?.authorities ?? [];
  const allowed = canAccessAdminPage(definition.id, authorities, session.phase);
  if (definition.id === "ADM-S04" && isRestrictedViewer(authorities)) return <Navigate replace to="/membership-status" />;
  const actor = session.actor;
  if (!actor) return <Navigate replace to="/login" />;
  const navigationPath = (screen: AdminNavigationScreenId) => (
    safeAdminReturnPath(menuPaths[screen]) ?? pathForAdminScreen(screen)
  );
  return (
    <AdminLayout
      active={definition.navigationScreen}
      identity={actor}
      authorities={authorities}
      onLogout={() => sessionLifecycle.end("logout")}
      onNavigate={(screen) => navigate(navigationPath(screen))}
      navigationHref={navigationPath}
      visibleScreens={visibleOperationScreens(authorities)}
      routeKey={location.pathname}
      routePath={`${location.pathname}${location.search}`}
      routeDataLoading={routeDataLoading}
    >
      <AdminNavigationEntryTracker definition={definition} />
      {allowed ? <Outlet /> : <StateNotice state="denied" />}
    </AdminLayout>
  );
}

function AdminNotFoundRoute() {
  const { sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  const location = useLocation();
  const navigate = useNavigate();
  const destination = phaseDestination(session.phase);
  if (session.phase === "anonymous") return <Navigate replace to={loginPathForLocation(location)} />;
  if (destination) return <Navigate replace to={`${destination}${returnToSearch(location.search)}`} />;
  if (!session.actor) return <Navigate replace to="/login" />;
  const authorities = session.authSession?.authorities ?? [];
  return (
    <AdminLayout
      active="04"
      identity={session.actor}
      authorities={authorities}
      onLogout={() => sessionLifecycle.end("logout")}
      onNavigate={(screen) => navigate(pathForAdminScreen(screen))}
      navigationHref={pathForAdminScreen}
      visibleScreens={visibleOperationScreens(authorities)}
      routeKey={location.pathname}
      routePath={`${location.pathname}${location.search}`}
    >
      <StateNotice state="not-found" />
    </AdminLayout>
  );
}

function AdminRouteError() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : "페이지를 표시하는 중 오류가 발생했습니다.";
  return (
    <main className="admin-auth">
      <div className="admin-auth__surface">
        <Alert
          persistent
          variant="danger"
          title="관리자 페이지를 표시하지 못했습니다"
          action={<Button onClick={() => window.location.assign("/")}>서비스 현황으로</Button>}
        >
          {message}
        </Alert>
      </div>
    </main>
  );
}

function childRoute(definition: AdminRouteDefinition, basePath = ""): RouteObject {
  const relativePath = definition.path.slice(basePath.length).replace(/^\//, "");
  return {
    ...(relativePath ? { path: relativePath } : { index: true }),
    element: <AdminRouteEntry definition={definition} />,
    errorElement: <AdminRouteError />,
    handle: { adminRoute: definition } satisfies AdminRouteHandle,
  };
}

const loginDefinition = ADMIN_ROUTES_BY_ID["ADM-S01"];
const setupDefinitions = ADMIN_ROUTE_DEFINITIONS.filter((definition) => definition.surface === "setup");
const operationDefinitions = ADMIN_ROUTE_DEFINITIONS.filter((definition) => definition.surface === "operations");

export const adminRouteObjects: RouteObject[] = [
  {
    path: loginDefinition.path,
    element: <AdminLoginRoute />,
    errorElement: <AdminRouteError />,
    handle: { adminRoute: loginDefinition } satisfies AdminRouteHandle,
  },
  {
    path: "/setup",
    element: <AdminSetupLayout />,
    errorElement: <AdminRouteError />,
    children: setupDefinitions.map((definition) => childRoute(definition, "/setup")),
  },
  {
    path: "/",
    element: <AdminOperationsLayout />,
    errorElement: <AdminRouteError />,
    children: operationDefinitions.map((definition) => childRoute(definition)),
  },
  {
    path: "*",
    element: <AdminNotFoundRoute />,
    errorElement: <AdminRouteError />,
  },
];

export const adminRouter = createBrowserRouter(adminRouteObjects);

export function AdminRouterProvider() {
  return <RouterProvider router={adminRouter} />;
}
